import { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, formatHour, SPORT_COLORS } from "@/lib/utils";
import { getImageUrl } from "@/lib/images";
import { Calendar, CalendarDays, Clock, RefreshCw, AlertCircle, ExternalLink, Sun, Sunrise, Sunset, Archive, Navigation, Share2, Check, MapPin } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function todayStr() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function tomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isPaidStatus(ps) {
  return ps && !ps.startsWith("pending");
}

function bookingDisplayStatus(b) {
  const paid = isPaidStatus(b.payment_status);
  const endHour = b.end_hour;
  const dateStr = b.date;
  const now = new Date();
  const bookingEnd = new Date(dateStr + "T" + String(Math.floor(endHour)).padStart(2, "0") + ":" + String(Math.round((endHour % 1) * 60)).padStart(2, "0") + ":00");
  if (b.status === "confirmed" && paid && bookingEnd < now) return "completed";
  return b.status;
}

function isPast(b) {
  const d = bookingDisplayStatus(b);
  if (d === "completed" || d === "cancelled" || d === "rejected") return true;
  if (b.date < todayStr()) return true;
  return false;
}

function dateGroupKey(dateStr) {
  const today = todayStr();
  const tomorrow = tomorrowStr();
  if (dateStr === today) return "today";
  if (dateStr === tomorrow) return "tomorrow";
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date();
  const weekEnd = new Date(now);
  weekEnd.setDate(now.getDate() + (6 - now.getDay()));
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  if (d > now && d <= weekEnd) return "this_week";
  if (d > weekEnd) return "later";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d >= yesterday) return "yesterday";
  if (d >= weekStart) return "last_week";
  return "older";
}

const STATUS_STYLES = {
  confirmed: "border-volt text-volt",
  pending: "border-yellow-400 text-yellow-400",
  cancelled: "border-border/60 text-muted-foreground/80",
  rejected: "border-red-400 text-red-400",
  checked_in: "border-sky-400 text-sky-400",
  completed: "border-emerald-400 text-emerald-400",
};

const PAYMENT_STYLES = {
  mocked_paid: "border-emerald-400 text-emerald-400",
  pending_cod: "border-yellow-400 text-yellow-400",
  paid_at_court: "border-volt text-volt",
  pending_online: "border-yellow-400 text-yellow-400",
};

const GROUP_ICONS = {
  today: <Sun className="h-4 w-4 text-volt" />,
  tomorrow: <Sunrise className="h-4 w-4 text-volt" />,
  this_week: <CalendarDays className="h-4 w-4 text-volt" />,
  later: <Calendar className="h-4 w-4 text-volt" />,
  yesterday: <Sunset className="h-4 w-4 text-volt" />,
  last_week: <CalendarDays className="h-4 w-4 text-volt" />,
  older: <Archive className="h-4 w-4 text-volt" />,
};
const GROUP_KEYS_UPCOMING = ["today", "tomorrow", "this_week", "later"];
const GROUP_KEYS_PAST = ["yesterday", "last_week", "older"];

function formatDateDisplay(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const days = ["SUN","MON","TUE","WED","THU","FRI","SAT"];
  return {
    day: days[d.getDay()],
    date: d.getDate(),
    month: months[d.getMonth()],
  };
}

function BookingCard({ booking, tab }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const status = bookingDisplayStatus(booking);
  const bookingUid = typeof booking.booking_uid === "string" ? booking.booking_uid.trim() : "";
  const needsPayment = booking.payment_status === "pending_cod" && booking.status !== "cancelled" && booking.status !== "rejected";
  const sportColor = SPORT_COLORS[booking.sport]?.bg;
  const sportBg = sportColor || "#444";
  const dateDisplay = formatDateDisplay(booking.date);

  const sportName = booking.sport
    ? booking.sport.charAt(0).toUpperCase() + booking.sport.slice(1)
    : "";

  const isUpcoming = tab === "upcoming";
  const isCancelled = tab === "cancelled";
  const deposit = booking.deposit_held || 0;
  const remaining = deposit > 0 ? booking.total_price - deposit : 0;
  const hasDeposit = deposit > 0 && deposit < booking.total_price;
  const hasDirections = Boolean((booking.lat && booking.lng) || booking.address);

  const openDirections = (e) => {
    e.stopPropagation();
    if (!hasDirections) {
      toast.error(t("bookings.directions_unavailable"));
      return;
    }
    if (booking.lat && booking.lng) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${booking.lat},${booking.lng}`, '_blank');
    } else if (booking.address) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(booking.address)}`, '_blank');
    }
  };

  const shareBooking = (e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/s/${booking.id}`;
    if (navigator.share) {
      navigator.share({ title: booking.court_name, text: `${booking.court_name} - ${booking.date} ${formatHour(booking.start_hour)}`, url });
    } else {
      navigator.clipboard.writeText(url).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <div
      data-testid={`booking-row-${booking.id}`}
      className="grid grid-cols-[3px_1fr] transition-colors group"
    >
      <div
        className="h-full w-full"
        style={{ backgroundColor: sportBg }}
      />

      <div className="border border-border bg-card hover:border-border transition-colors cursor-pointer"
        onClick={() => navigate(`/bookings/${booking.id}`)}
      >
        {needsPayment && (
          <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 bg-yellow-400/[0.04] border-b border-yellow-400/10 text-[9px] tracking-wider uppercase text-yellow-400">
            <AlertCircle className="h-3 w-3 shrink-0" />
            {t("bookings.unpaid_banner")}
          </div>
        )}

        <div className="grid grid-cols-[60px_1fr_auto] sm:grid-cols-[72px_1fr_auto] gap-3 sm:gap-4 p-3 sm:p-4">
          <div className="flex flex-col self-stretch">
            {booking.court_image ? (
              <div className="h-14 sm:h-16 w-full overflow-hidden border border-border/30">
                <img src={getImageUrl(booking.court_image, 'thumbnail')} alt="" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="h-14 sm:h-16 w-full bg-muted border border-border/30" />
            )}
            <div className="flex flex-col items-center justify-center border border-border border-t-0 bg-background py-1 px-1 text-center flex-1">
              <span className="text-[8px] tracking-widest text-muted-foreground/80 font-bold">{dateDisplay.day}</span>
              <span className="font-heading text-base sm:text-lg leading-none text-foreground">{dateDisplay.date}</span>
              <span className="text-[8px] tracking-widest text-muted-foreground/80 font-bold">{dateDisplay.month}</span>
            </div>
          </div>

          <div className="flex flex-col justify-between min-w-0 py-0.5">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                {sportName && (
                  <span
                    className="text-[9px] px-1.5 py-px tracking-wider uppercase font-bold"
                    style={{ backgroundColor: sportBg, color: SPORT_COLORS[booking.sport]?.text || "#fff" }}
                  >
                    {sportName}
                  </span>
                )}
                <span className="font-heading text-sm sm:text-lg tracking-tight truncate">
                  {booking.court_name}
                </span>
              </div>

              <div className="flex items-center gap-1.5 mt-1">
                <Clock className="h-3.5 w-3.5 text-volt shrink-0" />
                <span className="text-xs sm:text-sm text-foreground/80 font-medium tabular-nums">
                  {formatHour(booking.start_hour)} — {formatHour(booking.end_hour)}
                </span>
                {booking.city && (
                  <>
                    <span className="text-muted-foreground/40 mx-0.5">·</span>
                    <MapPin className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                    <span className="text-[10px] text-muted-foreground/80 truncate">{booking.city}</span>
                  </>
                )}
              </div>

              {bookingUid && (
                <div className="mt-1.5 flex items-center gap-1.5 min-w-0 flex-wrap">
                  <span className="text-[9px] sm:text-[10px] tracking-widest uppercase text-muted-foreground/70 shrink-0">
                    {t("bookings.booking_ref")}
                  </span>
                  <span className="max-w-full truncate border border-border/60 bg-background px-1.5 py-0.5 font-mono text-[10px] sm:text-xs text-foreground/85">
                    {bookingUid}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 sm:gap-3 mt-1 flex-wrap">
              <span className={`text-[9px] sm:text-[10px] tracking-widest uppercase border px-2 py-0.5 font-bold ${STATUS_STYLES[status] || "border-border text-muted-foreground/80"}`}>
                {t(`bookings.status_${status}`, status)}
              </span>
              {booking.payment_status && (
                <span className={`text-[9px] sm:text-[10px] tracking-widest uppercase border px-2 py-0.5 font-bold ${PAYMENT_STYLES[booking.payment_status] || "border-border text-muted-foreground/80"}`}>
                  {t(`bookings.payment.${booking.payment_status}`, booking.payment_status)}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end justify-between py-0.5">
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-volt font-mono text-sm sm:text-lg font-bold tabular-nums">
                {formatPrice(booking.total_price, booking.currency)}
              </span>
              {hasDeposit && (
                <span className="text-[9px] text-muted-foreground/80 tabular-nums leading-none">
                  {isCancelled || !isUpcoming
                    ? formatPrice(booking.total_price, booking.currency)
                    : `${formatPrice(remaining, booking.currency)} ${t("bookings.remaining")}`
                  }
                </span>
              )}
            </div>

            {isUpcoming && (
              <div className="flex flex-col items-end gap-1.5">
                <Link
                  to={`/courts/${booking.court_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1.5 text-[10px] text-muted-foreground/80 hover:text-volt transition-colors"
                  title={t("booking_details.view_court")}
                  aria-label={t("booking_details.view_court")}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t("bookings.court_link")}</span>
                </Link>
                <button
                  onClick={openDirections}
                  disabled={!hasDirections}
                  className="flex items-center gap-1.5 text-[10px] text-muted-foreground/80 hover:text-volt transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title={t("bookings.get_directions")}
                  aria-label={t("bookings.get_directions")}
                >
                  <Navigation className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t("bookings.get_directions")}</span>
                </button>
                <button
                  onClick={shareBooking}
                  className="flex items-center gap-1.5 text-[10px] text-muted-foreground/80 hover:text-volt transition-colors"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Share2 className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{copied ? t("common.copied") : t("bookings.share")}</span>
                </button>
              </div>
            )}

            {!isUpcoming && (
              <Link
                to={`/courts/${booking.court_id}?date=${todayStr()}&start=${booking.start_hour}&end=${booking.end_hour}`}
                onClick={(e) => e.stopPropagation()}
                className="btn-outline rounded-sm px-3 py-1.5 text-[10px] sm:text-xs flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" /> <span className="hidden sm:inline">{t("bookings.book_again")}</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Bookings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");
  const [filter, setFilter] = useState("all");
  const [showNotifDialog, setShowNotifDialog] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/bookings").then((r) => setBookings(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      setShowNotifDialog(true);
    }
  }, []);

  const { upcoming, past, cancelled } = useMemo(() => {
    const u = bookings.filter((b) => !isPast(b));
    const p = bookings.filter((b) => isPast(b) && b.status !== "cancelled" && b.status !== "rejected");
    const c = bookings.filter((b) => b.status === "cancelled" || b.status === "rejected");
    u.sort((a, b) => (a.date + a.start_hour).localeCompare(b.date + b.start_hour));
    p.sort((a, b) => (b.date + b.start_hour).localeCompare(a.date + a.start_hour));
    c.sort((a, b) => (b.date + b.start_hour).localeCompare(a.date + a.start_hour));
    return { upcoming: u, past: p, cancelled: c };
  }, [bookings]);

  const needsAction = useMemo(() => {
    return bookings.filter((b) => {
      if (b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected") return true;
      if (b.status === "pending") return true;
      return false;
    });
  }, [bookings]);

  const showActionBar = needsAction.length > 0;

  const activeList = tab === "upcoming" ? upcoming : tab === "past" ? past : cancelled;
  const groupKeys = tab === "upcoming" ? GROUP_KEYS_UPCOMING : GROUP_KEYS_PAST;

  const grouped = useMemo(() => {
    const m = new Map();
    for (const b of activeList) {
      const k = dateGroupKey(b.date);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(b);
    }
    const ordered = groupKeys.filter((k) => m.has(k));
    const extras = Array.from(m.keys()).filter((k) => !groupKeys.includes(k));
    return [...ordered, ...extras].map((k) => ({ key: k, bookings: m.get(k) }));
  }, [activeList, groupKeys]);

  const filtered = useMemo(() => {
    if (filter === "all") return grouped;
    if (filter === "needs_action") {
      return grouped.map((g) => ({
        ...g,
        bookings: g.bookings.filter((b) => {
          if (b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected") return true;
          if (b.status === "pending") return true;
          return false;
        }),
      })).filter((g) => g.bookings.length > 0);
    }
    return grouped.map((g) => ({
      ...g,
      bookings: g.bookings.filter((b) => {
        if (filter === "needs_payment") return b.payment_status === "pending_cod" && b.status !== "cancelled" && b.status !== "rejected";
        if (filter === "pending") return b.status === "pending";
        if (filter === "confirmed") return b.status === "confirmed";
        return true;
      }),
    })).filter((g) => g.bookings.length > 0);
  }, [grouped, filter]);

  const showFilters = tab !== "cancelled" && activeList.length > 3;

  const handleActionBarClick = () => {
    setTab("upcoming");
    setFilter("needs_action");
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-background">
      <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-10">
        <div className="label-eyebrow text-volt mb-2">{t("bookings.eyebrow")}</div>
        <h1 className="font-heading text-5xl md:text-6xl tracking-tight mb-8">{t("bookings.title")}</h1>

        {loading ? (
          <div className="flex flex-col items-center py-20 text-muted-foreground/60">
            <Calendar className="h-12 w-12 mb-4 animate-pulse" />
            <p className="text-sm tracking-widest uppercase">{t("common.loading")}</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center">
            <Calendar className="h-12 w-12 text-muted-foreground/20 mb-6" />
            <p className="font-heading text-3xl text-muted-foreground/60">{t("bookings.none")}</p>
            <p className="text-muted-foreground/80 text-sm mt-2 mb-8 max-w-xs">{t("bookings.empty_subtitle")}</p>
            <Link to="/search">
              <button className="btn-volt px-6 py-3 text-xs">{t("bookings.find_court")}</button>
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div className="flex gap-1 border border-border bg-card p-1">
                <button
                  onClick={() => { setTab("upcoming"); setFilter("all"); }}
                  className={`px-4 py-2 text-[10px] sm:text-xs tracking-widest uppercase font-bold transition-all ${
                    tab === "upcoming"
                      ? "bg-volt text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("bookings.tab_upcoming")} ({upcoming.length})
                </button>
                <button
                  onClick={() => { setTab("past"); setFilter("all"); }}
                  className={`px-4 py-2 text-[10px] sm:text-xs tracking-widest uppercase font-bold transition-all ${
                    tab === "past"
                      ? "bg-volt text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("bookings.tab_past")} ({past.length})
                </button>
                <button
                  onClick={() => { setTab("cancelled"); setFilter("all"); }}
                  className={`px-4 py-2 text-[10px] sm:text-xs tracking-widest uppercase font-bold transition-all ${
                    tab === "cancelled"
                      ? "bg-volt text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {t("bookings.tab_cancelled")} ({cancelled.length})
                </button>
              </div>
              {showFilters && (
                <div className="flex gap-1">
                  {[
                    { key: "all", label: t("bookings.filter_all") },
                    { key: "needs_payment", label: t("bookings.filter_needs_payment") },
                    { key: "pending", label: t("bookings.filter_pending") },
                    { key: "confirmed", label: t("bookings.filter_confirmed") },
                  ].map((f) => (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key)}
                      className={`px-3 py-1.5 text-[9px] tracking-widest uppercase border transition-all ${
                        filter === f.key
                          ? "border-volt text-volt font-bold"
                          : "border-border text-muted-foreground/80 font-semibold hover:text-foreground hover:border-foreground/30"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {showActionBar && tab === "upcoming" && filter === "all" && (
              <div
                onClick={handleActionBarClick}
                className="flex items-center justify-between px-4 py-3 mb-6 border border-yellow-400/20 bg-yellow-400/[0.03] cursor-pointer hover:border-yellow-400/40 transition-colors"
              >
                <div className="flex items-center gap-2 text-xs text-yellow-400">
                  <AlertCircle className="h-4 w-4" />
                  <span>
                    {needsAction.length === 1
                      ? t("bookings.needs_action_bar_one")
                      : t("bookings.needs_action_bar", { count: needsAction.length })}
                  </span>
                </div>
                <span className="flex items-center gap-1 text-[10px] tracking-widest uppercase text-yellow-400/60 hover:text-yellow-400 transition-colors">
                  {t("bookings.show_action_only")} <ExternalLink className="h-3 w-3" />
                </span>
              </div>
            )}

            {activeList.length === 0 ? (
              <div className="flex flex-col items-center py-20 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground/20 mb-6" />
                <p className="font-heading text-2xl text-muted-foreground/60">
                  {tab === "upcoming" ? t("bookings.no_upcoming") : tab === "past" ? t("bookings.no_past") : t("bookings.no_cancelled")}
                </p>
                <p className="text-muted-foreground/80 text-sm mt-2 mb-8 max-w-xs">
                  {tab === "upcoming" ? t("bookings.no_upcoming_hint") : tab === "past" ? t("bookings.no_past_hint") : t("bookings.no_cancelled_hint")}
                </p>
                {tab === "upcoming" && (
                  <Link to="/search">
                    <button className="btn-volt px-6 py-3 text-xs">{t("bookings.find_court")}</button>
                  </Link>
                )}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center py-20 text-center">
                <Calendar className="h-12 w-12 text-muted-foreground/20 mb-6" />
                <p className="font-heading text-2xl text-muted-foreground/60">{t("bookings.no_results")}</p>
                <p className="text-muted-foreground/80 text-sm mt-2 mb-6">{t("bookings.no_results_hint")}</p>
                <button
                  onClick={() => setFilter("all")}
                  className="btn-outline rounded-sm px-6 py-3 text-xs"
                >
                  {t("bookings.clear_filters")}
                </button>
              </div>
            ) : (
              <div className="space-y-8">
                {filtered.map((group) => (
                  <div key={group.key}>
                    <div className="flex items-center gap-2 mb-3 px-1">
                      {GROUP_ICONS[group.key] || <Calendar className="h-4 w-4 text-volt" />}
                      <span className="label-eyebrow text-volt">
                        {t(`bookings.group_${group.key}`, group.key)}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {group.bookings.map((b) => (
                        <BookingCard key={b.id} booking={b} tab={tab} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <AlertDialog open={showNotifDialog} onOpenChange={setShowNotifDialog}>
        <AlertDialogContent className="bg-card border border-border text-foreground rounded-none p-8">
          <AlertDialogHeader className="text-center sm:text-start">
            <div className="flex items-center justify-center sm:justify-start mb-1">
              <span className="text-3xl" role="img" aria-hidden="true">🔔</span>
            </div>
            <AlertDialogTitle className="font-heading text-3xl tracking-tight">{t("booking_reminder.enable_title")}</AlertDialogTitle>
            <AlertDialogDescription className="text-muted-foreground/80 text-sm leading-relaxed">
              {t("booking_reminder.enable_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="h-px bg-border/30 my-2" />
          <AlertDialogFooter className="flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <AlertDialogCancel className="rounded-none border border-border bg-transparent text-muted-foreground/80 hover:bg-muted hover:text-foreground px-5 py-2.5 text-xs tracking-widest uppercase font-semibold">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none bg-volt text-primary-foreground hover:bg-white font-bold text-xs tracking-widest uppercase px-5 py-2.5"
              onClick={() => Notification.requestPermission()}
            >
              {t("booking_reminder.enable_btn")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
